import React, { useRef, useState } from "react";
import { Camera, FileText } from "lucide-react";
import moment from "moment";

import BottomSheet from "src/components/app/BottomSheet";
import CameraUpload from "src/components/common/CameraUpload";

import useThemeColors from "src/hooks/useThemeColors";
import { useAppSelector } from "src/redux/hooks";

interface DocumentSelectBottomSheetProps {
  show: boolean;
  documentName: string;
  handleClose: () => void;
  onFileUpload: (fileData: any) => void;
}

const DocumentSelectBottomSheet: React.FC<DocumentSelectBottomSheetProps> = ({
  show,
  documentName,
  handleClose,
  onFileUpload,
}) => {
  const color = useThemeColors();
  const { getTestTypes } = useAppSelector((state) => state.records);
  const fileInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const [showCamera, setShowCamera] = useState(false);
  const [isPdfFile, setIsPdfUpload] = useState(false);
  // const [showDocumentDetails, setShowDocumentDetails] = useState(false);
  const [documentDetails, setDocumentDetails] = useState({
    fileData: null,
    documentName: documentName || `New Record - ${moment().unix()}`,
    documentType: "",
    documentImage: "",
  });

  const handleCameraClick = () => {
    setShowCamera(true);
  };

  const handleGalleryClick = () => {
    //handleClose();
    (galleryInputRef.current as HTMLInputElement | null)?.click();
  };

  const handleFileClick = () => {
    //handleClose();
    (fileInputRef.current as HTMLInputElement | null)?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []).map((file) => {
      // Ensure mime type is set
      if (!file.type) {
        // Determine mime type based on file extension
        const extension = file.name.split(".").pop()?.toLowerCase();
        const mimeTypes = {
          pdf: "application/pdf",
          jpg: "image/jpeg",
          jpeg: "image/jpeg",
          png: "image/png",
        };
        return new File([file], file.name, {
          type:
            mimeTypes[extension as keyof typeof mimeTypes] ||
            "application/octet-stream",
        });
      }
      return file;
    });
    // onFileUpload({ fileData: files });
    setIsPdfUpload(files[0].type === "application/pdf");
    handleDocumentDetails({ fileData: files });
  };

  const handleCameraDone = async (images: any) => {
    // console.log("images", images);
    // conver this to file and call handleDocumentDetails
    const filePromises = await Promise.all(
      images.map((imageData: any, index: number) =>
        fetch(imageData)
          .then((res) => res.blob())
          .then((blob) => {
            return new File([blob], `camera_${index + 1}.jpg`, {
              type: "image/jpeg",
            });
          })
      )
    );

    // console.log("filePromises", filePromises);

    handleDocumentDetails({ fileData: filePromises });

    // Handle multiple captured images
    // const filePromises = await Promise.all(
    //   images.map((imageData: any, index: number) =>
    //     fetch(imageData)
    //       .then((res) => res.blob())
    //       .then((blob) => {
    //         return new File([blob], `camera_${index + 1}.jpg`, {
    //           type: "image/jpeg",
    //         });
    //       })
    //   )
    // );

    // // Wait for all files to be processed and send them together
    // Promise.all(filePromises)
    //   .then((files) => {
    //     // onFileUpload({ fileData: files });
    //     handleDocumentDetails({ fileData: files });
    //   })
    //   .catch((error) => {
    //     console.error("Error processing images:", error);
    //   });
  };

  const handleDocumentDetails = async (fileData: any) => {
    // console.log("handleDocumentDetails:fileData", fileData);
    const documentDetailsNew = {
      ...documentDetails,
      fileData: fileData.fileData,
      documentImage: URL.createObjectURL(fileData.fileData[0]),
    };
    setDocumentDetails(documentDetailsNew);
    // setShowDocumentDetails(true);
    onFileUpload({
      fileData: documentDetailsNew.fileData,
      documentDetails: documentDetailsNew,
    });
  };

  // const handleSaveDocument = () => {
  //   onFileUpload({
  //     fileData: documentDetails.fileData,
  //     documentDetails: documentDetails,
  //   });
  //   setShowDocumentDetails(false);
  //   setDocumentDetails({
  //     fileData: null,
  //     documentName: `New Record - ${moment().unix()}`,
  //     documentType: "",
  //     documentImage: "",
  //   });
  // };

  return (
    <React.Fragment>
      <BottomSheet
        show={show && !showCamera}
        title="Document Upload"
        handleClose={handleClose}
        showCloseButton
      >
        <div className="relative w-full">
          <div className="flex flex-col">
            {[
              {
                label: "Use Camera",
                icon: <Camera className="w-4 h-4" />,
                onClick: handleCameraClick,
              },
              {
                label: "Upload from Gallery",
                icon: <FileText className="w-4 h-4" />,
                onClick: handleGalleryClick,
              },
              {
                label: "Upload from Files",
                icon: <FileText className="w-4 h-4" />,
                onClick: handleFileClick,
              },
            ].map((item, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2 border-b border-gray-300 px-4 py-3 text-gray-700 cursor-pointer"
                onClick={item.onClick}
              >
                <div className="p-2 rounded-full bg-[var(--icon-bg-primary)] text-[var(--app-primary)]">
                  {item.icon}
                </div>
                <span className="text-base">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </BottomSheet>

      {/* Hidden File Inputs */}
      <input
        type="file"
        ref={fileInputRef}
        accept="application/pdf"
        onChange={handleFileChange}
        className="hidden"
      />
      <input
        type="file"
        ref={galleryInputRef}
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />

      {showCamera && (
        <CameraUpload
          onClose={() => {
            setShowCamera(false);
            handleClose();
          }}
          onCapture={() => {}}
          onDone={handleCameraDone}
        />
      )}

      {/* DOCUMENT DETAILS */}
      {/* {showDocumentDetails && (
        <div className="fixed top-0 bottom-0 left-0 right-0 w-full h-full z-50 bg-white">
          <div className="fixed top-0 left-0 right-0 z-50">
            <Header
              title="Document Details"
              onBackClick={() => setShowDocumentDetails(false)}
            />
          </div>
          <div className="w-full h-[calc(100vh-64px)] flex flex-col justify-between mt-16 overflow-y-auto">
            <div className="w-full p-4 space-y-8">
              <div className="relative w-full px-6">
                <div
                  className="relative w-full h-40 bg-gray-100 rounded-xl border-4 border-secondary"
                  style={{
                    boxShadow: `7px 7px 1px ${color.secondary}bb`,
                  }}
                >
                  <div className="absolute top-1/2 -translate-y-1/2 -left-2 w-6 h-1/2 bg-secondary rounded-lg">
                    <div className="absolute w-10 h-10 bg-secondary rounded-full -left-4 top-1/2 -translate-y-1/2"></div>
                  </div>
                  <div className="w-full h-full rounded-lg overflow-hidden">
                    {isPdfFile ? (
                      <img
                        src={require("src/assets/images/icons/pdf-file.png")}
                        alt=""
                        className="w-full h-full object-contain p-2"
                      />
                    ) : (
                      <img
                        src={documentDetails.documentImage}
                        alt=""
                        className="w-full h-full object-fill"
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className="w-full">
                <Input
                  label="Document Name"
                  type="text"
                  name="documentName"
                  value={documentDetails.documentName}
                  onChange={(e) =>
                    setDocumentDetails({
                      ...documentDetails,
                      documentName: e.target.value,
                    })
                  }
                  fullWidth
                  placeholder="Enter Document Name"
                />
              </div>
              <div className="w-full">
                <h2 className="text-sm font-medium mb-1">Select Record Type</h2>
                <div className="flex items-center gap-3 flex-wrap">
                  {(getTestTypes.data?.test_type || [])?.map(
                    (type: any, idx: number) => (
                      <button
                        key={idx}
                        className={`inline-block py-1 px-3 text-sm font-light border border-gray-300 rounded-3xl ${
                          documentDetails.documentType === type.test_name
                            ? "bg-[var(--icon-bg-primary)] border-[var(--app-primary)] text-[var(--app-primary)]"
                            : "bg-transparent border-gray-300 text-gray-500"
                        }`}
                        onClick={() =>
                          setDocumentDetails({
                            ...documentDetails,
                            documentType: type.test_name,
                          })
                        }
                      >
                        {type.test_name}
                      </button>
                    )
                  )}
                </div>
              </div>
            </div>
            <div className="w-full p-4">
              <Button
                fullWidth
                onClick={handleSaveDocument}
                disabled={
                  !documentDetails.documentName || !documentDetails.documentType
                }
              >
                Save Document
              </Button>
            </div>
          </div>
        </div>
      )} */}
    </React.Fragment>
  );
};

export default DocumentSelectBottomSheet;
